import { getScopedDomain } from '../lib/tenantScope';
// E911 / Kari's Law dispatchable locations.
// Tenant-wide or per-department street addresses. 911 dispatch sees this on emergency calls.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, MapPin, X } from 'lucide-react';
import { api, getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function E911Locations() {
  const me = api.me();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);

  const tenants = useQuery<any[]>({
    queryKey: ['tenants'],
    queryFn: () => fetch('/api/tenants', auth()).then(r => r.json()),
  });

  const { data: list = [] } = useQuery({
    queryKey: ['group-geo', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.groupGeolocation.list({ domain_uuid: getScopedDomain(me?.domain_uuid) }) : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.groupGeolocation.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group-geo', getScopedDomain(me?.domain_uuid)] }),
  });

  const groupName = (uuid: string | null) =>
    uuid ? (tenants.data || []).find(t => t.domain_uuid === uuid)?.domain_description ?? 'Tenant-wide' : 'Tenant-wide';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><MapPin /> E911 Locations</h1>
        <button className="btn-primary" onClick={() => setEditing({})}><Plus size={16} className="inline mr-1" /> Add Location</button>
      </div>

      <div className="card p-3 text-sm text-textSecondary">
        Required for Kari's Law / RAY BAUM compliance. Each location maps a department (or
        the whole tenant) to a dispatchable street address. Extensions in that department
        inherit this address when they dial 911.
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Department</th><th>Address</th><th></th></tr></thead>
          <tbody>
            {list.map((g: any) => (
              <tr key={g.geo_uuid} className="hover:bg-elevated cursor-pointer" onClick={() => setEditing(g)}>
                <td className="font-medium">{g.friendly_name}</td>
                <td className="text-sm text-textSecondary">{groupName(g.group_uuid)}</td>
                <td className="text-sm">{[g.street_address, g.city, g.state_region, g.postal_code, g.country].filter(Boolean).join(', ')}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete?') && remove.mutate(g.geo_uuid)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-textSecondary">No locations yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && getScopedDomain(me?.domain_uuid) && <EditModal domain_uuid={getScopedDomain(me?.domain_uuid)} loc={editing} tenants={tenants.data || []} onClose={() => setEditing(null)} />}
    </div>
  );
}

function EditModal({ domain_uuid, loc, tenants, onClose }: { domain_uuid: string; loc: any; tenants: any[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>(loc);

  const save = useMutation({
    mutationFn: () => loc.geo_uuid
      ? api.groupGeolocation.update(loc.geo_uuid, form)
      : api.groupGeolocation.create({ ...form, domain_uuid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group-geo', domain_uuid] });
      onClose();
    },
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{loc.geo_uuid ? 'Edit' : 'New'} Location</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <Field label="Friendly name *">
          <input className="input" value={form.friendly_name || ''} onChange={e => set('friendly_name', e.target.value)} placeholder="HQ Floor 2" />
        </Field>
        <Field label="Department" hint="Leave blank for tenant-wide">
          <select className="input" value={form.group_uuid || ''} onChange={e => set('group_uuid', e.target.value || null)}>
            <option value="">— Tenant-wide —</option>
            {tenants.map((t: any) => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
          </select>
        </Field>
        <Field label="Street address">
          <input className="input" value={form.street_address || ''} onChange={e => set('street_address', e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="City"><input className="input" value={form.city || ''} onChange={e => set('city', e.target.value)} /></Field>
          <Field label="State/Region"><input className="input" value={form.state_region || ''} onChange={e => set('state_region', e.target.value)} /></Field>
          <Field label="Postal"><input className="input" value={form.postal_code || ''} onChange={e => set('postal_code', e.target.value)} /></Field>
          <Field label="Country"><input className="input" value={form.country || 'US'} onChange={e => set('country', e.target.value)} /></Field>
        </div>
        <Field label="Additional info" hint="Floor, suite, building landmarks">
          <input className="input" value={form.additional_info || ''} onChange={e => set('additional_info', e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Latitude"><input className="input font-mono" type="number" step="0.000001" value={form.latitude || ''} onChange={e => set('latitude', e.target.value)} /></Field>
          <Field label="Longitude"><input className="input font-mono" type="number" step="0.000001" value={form.longitude || ''} onChange={e => set('longitude', e.target.value)} /></Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!form.friendly_name || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-textSecondary">{hint}</div>}
    </div>
  );
}
